import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FirebaseService } from '../../services/firebase.service';
import { AuthService } from '../../services/auth.service';
import { AssessmentResult } from '../../models/assessment.model';

// PrimeNG Imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { CalendarModule } from 'primeng/calendar';
import { SliderModule } from 'primeng/slider';
import { ToolbarModule } from 'primeng/toolbar';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TagModule,
    DropdownModule,
    MultiSelectModule,
    ProgressBarModule,
    CalendarModule,
    SliderModule,
    ToolbarModule,
    CardModule,
    TooltipModule
  ],
  template: `
    <!-- Table Section -->
    <div class="table-container">
      <p-card>
        <!-- Toolbar -->
        <p-toolbar class="table-toolbar">
          <div class="p-toolbar-group-start">
            <h2 class="table-title">Assessment Results ({{ getFilteredAssessments().length }})</h2>
          </div>
          <div class="p-toolbar-group-end">
            <p-button 
              icon="pi pi-refresh" 
              class="p-button-outlined" 
              (onClick)="refreshData()"
              pTooltip="Refresh Data">
            </p-button>
            <p-button 
              icon="pi pi-download" 
              class="p-button-outlined ml-2" 
              (onClick)="exportData()"
              pTooltip="Export Data">
            </p-button>
          </div>
        </p-toolbar>

        <!-- Data Table -->
        <p-table 
          #dt
          [value]="getFilteredAssessments()" 
          [rows]="10" 
          [paginator]="true" 
          [globalFilterFields]="['participantInfo.name','participantInfo.email','participantInfo.organisation','level']"
          [rowsPerPageOptions]="[5,10,20,50]"
          [loading]="loading"
          [responsive]="true"
          [scrollable]="true"
          scrollHeight="400px"
          [resizableColumns]="true"
          columnResizeMode="fit"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          styleClass="p-datatable-striped p-datatable-gridlines">
          
          <!-- Table Caption and Global Search -->
          <ng-template pTemplate="caption">
            <div class="table-header">
              <span class="p-input-icon-left search-container">
                <i class="pi pi-search"></i>
                <input 
                  pInputText 
                  type="text" 
                  (input)="dt.filterGlobal(onGlobalFilter($event), 'contains')" 
                  placeholder="Search assessments..." 
                  class="global-search" />
              </span>
              <div class="filter-container">
                <p-multiSelect 
                  [options]="levelOptions" 
                  [(ngModel)]="selectedLevels" 
                  placeholder="Filter by Level"
                  [filter]="false"
                  [showClear]="true"
                  (onChange)="onLevelFilter($event)"
                  styleClass="level-filter">
                </p-multiSelect>
              </div>
            </div>
          </ng-template>

          <!-- Table Columns -->
          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="participantInfo.name" class="name-col">
                <div class="header-content">
                  Name <p-sortIcon field="participantInfo.name"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="participantInfo.email" class="email-col">
                <div class="header-content">
                  Email <p-sortIcon field="participantInfo.email"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="participantInfo.organisation" class="org-col">
                <div class="header-content">
                  Organisation <p-sortIcon field="participantInfo.organisation"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="totalScore" class="score-col">
                <div class="header-content">
                  Score <p-sortIcon field="totalScore"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="level" class="level-col">
                <div class="header-content">
                  Level <p-sortIcon field="level"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="timestamp" class="date-col">
                <div class="header-content">
                  Date <p-sortIcon field="timestamp"></p-sortIcon>
                </div>
              </th>
              <th class="actions-col">Actions</th>
            </tr>
          </ng-template>

          <!-- Table Body -->
          <ng-template pTemplate="body" let-assessment let-expanded="expanded">
            <tr>
              <td class="name-cell">
                <div class="participant-info">
                  <strong>{{ assessment.participantInfo?.name || 'Anonymous' }}</strong>
                </div>
              </td>
              <td class="email-cell">
                <span class="email-text">{{ assessment.participantInfo?.email || 'N/A' }}</span>
              </td>
              <td class="org-cell">
                <span class="org-text">{{ assessment.participantInfo?.organisation || 'N/A' }}</span>
              </td>
              <td class="score-cell">
                <div class="score-display">
                  <span class="score-number">{{ assessment.totalScore }}</span>
                  <p-progressBar 
                    [value]="(assessment.totalScore / 40) * 100" 
                    [showValue]="false"
                    styleClass="score-progress">
                  </p-progressBar>
                </div>
              </td>
              <td class="level-cell">
                <p-tag 
                  [value]="assessment.level" 
                  [severity]="getLevelSeverity(assessment.level)"
                  styleClass="level-tag">
                </p-tag>
              </td>
              <td class="date-cell">
                <span class="date-text">{{ assessment.timestamp | date:'short' }}</span>
              </td>
              <td class="actions-cell">
                <p-button 
                  icon="pi pi-eye" 
                  class="p-button-text p-button-sm" 
                  (onClick)="viewDetails(assessment)"
                  pTooltip="View Details">
                </p-button>
                <p-button 
                  [icon]="expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" 
                  class="p-button-text p-button-sm ml-1" 
                  (onClick)="expandRow(assessment)"
                  pTooltip="Toggle Details">
                </p-button>
              </td>
            </tr>
          </ng-template>

          <!-- Expandable Row Template -->
          <ng-template pTemplate="rowexpansion" let-assessment>
            <tr>
              <td colspan="7">
                <div class="assessment-details">
                  <h4>Assessment Breakdown</h4>
                  <div class="details-grid">
                    <div class="detail-section">
                      <h5>Intellectual Ability</h5>
                      <div class="score-item">
                        <span>Advice & Expertise:</span>
                        <span class="score">{{ assessment.selfAssessment?.intellectualAdvice || 0 }}/5</span>
                      </div>
                      <div class="score-item">
                        <span>Formal Qualifications:</span>
                        <span class="score">{{ assessment.selfAssessment?.formalQualifications || 0 }}/5</span>
                      </div>
                    </div>
                    
                    <div class="detail-section">
                      <h5>Financial Capacity</h5>
                      <div class="score-item">
                        <span>Investment Ability:</span>
                        <span class="score">{{ assessment.selfAssessment?.financialInvestment || 0 }}/5</span>
                      </div>
                      <div class="score-item">
                        <span>Long-term Assets:</span>
                        <span class="score">{{ assessment.selfAssessment?.longTermAssets || 0 }}/5</span>
                      </div>
                    </div>
                    
                    <div class="detail-section">
                      <h5>Social Influence</h5>
                      <div class="score-item">
                        <span>Role Model:</span>
                        <span class="score">{{ assessment.selfAssessment?.roleModel || 0 }}/5</span>
                      </div>
                      <div class="score-item">
                        <span>Network Connector:</span>
                        <span class="score">{{ assessment.selfAssessment?.networkConnector || 0 }}/5</span>
                      </div>
                    </div>
                    
                    <div class="detail-section">
                      <h5>Professional Position</h5>
                      <div class="score-item">
                        <span>Leadership Roles:</span>
                        <span class="score">{{ assessment.selfAssessment?.leadershipRoles || 0 }}/5</span>
                      </div>
                      <div class="score-item">
                        <span>Mentor/Trainer:</span>
                        <span class="score">{{ assessment.selfAssessment?.mentorTrainer || 0 }}/5</span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ng-template>

          <!-- Empty State -->
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="7" class="empty-state">
                <div class="empty-content">
                  <i class="pi pi-inbox empty-icon"></i>
                  <h3>No assessments found</h3>
                  <p>There are no assessment results to display.</p>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </div>
  `,
  styles: [`
    .dashboard-header {
      padding: 1rem;
      background: linear-gradient(135deg, #667eea 0%, rgb(251 22 54) 100%);
      margin-bottom: 2rem;
    }

    td div, td span {
      padding: 10px;
    }

    .pi-search {
      position: relative;
      left: calc(100% - 30px);
      top: 34px;
      display: block;
      width: 20px;
    }

    .nzf-logo {
      text-align: center;
      padding: 1rem;
    }

    .logo-img {
      width: min(200px, 80vw);
      max-width: 200px;
      height: auto;
    }

    .hero-card {
      max-width: 600px;
      margin: 0 auto;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
    }

    .hero-title {
      font-size: clamp(1.5rem, 4vw, 2.5rem);
      font-weight: 700;
      color: #1a202c;
      margin-bottom: 0.5rem;
      text-align: center;
    }

    .hero-description {
      font-size: clamp(1rem, 2.5vw, 1.125rem);
      color: #4a5568;
      text-align: center;
      margin: 0;
    }

    .table-container {
      padding: 0 1rem 2rem;
      max-width: 1400px;
      margin: 0 auto;
    }

    .table-toolbar {
      margin-bottom: 1rem;
      border-radius: 8px;
    }

    .table-title {
      margin: 0;
      font-size: 1.25rem;
      color: #374151;
    }

    .table-header {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      padding: 1rem;
      background: #f8fafc;
      border-radius: 8px;
    }

    .search-container {
      flex: 1;
      min-width: 250px;
    }

    .global-search {
      width: 100%;
      padding: 0.75rem;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      font-size: 1rem;
    }

    .filter-container {
      min-width: 200px;
    }

    .level-filter {
      width: 100%;
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      padding: 10px;
    }

    .name-col, .email-col, .org-col {
      min-width: 150px;
    }

    .score-col, .level-col, .date-col {
      min-width: 120px;
      text-align: center;
    }

    .actions-col {
      min-width: 100px;
      text-align: center;
    }

    .participant-info {
      display: flex;
      flex-direction: column;
    }

    .score-display {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
    }

    .score-number {
      font-weight: 600;
      font-size: 1.1rem;
    }

    .score-progress {
      width: 60px;
      height: 6px;
    }

    .level-tag {
      font-weight: 500;
    }

    .assessment-details {
      padding: 1.5rem;
      background: #f8fafc;
      border-radius: 8px;
      margin: 1rem;
    }

    .assessment-details h4 {
      margin: 0 0 1rem 0;
      color: #1f2937;
      font-size: 1.25rem;
    }

    .details-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
    }

    .detail-section h5 {
      margin: 0 0 0.75rem 0;
      color: #374151;
      font-size: 1rem;
      font-weight: 600;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 0.5rem;
    }

    .score-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0;
      border-bottom: 1px solid #f3f4f6;
    }

    .score-item:last-child {
      border-bottom: none;
    }

    .score-item .score {
      font-weight: 600;
      color: #059669;
    }

    .empty-state {
      text-align: center;
      padding: 3rem;
    }

    .empty-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      color: #6b7280;
    }

    .empty-icon {
      font-size: 3rem;
      color: #d1d5db;
    }

    .empty-content h3 {
      margin: 0;
      color: #374151;
    }

    .empty-content p {
      margin: 0;
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
      .table-header {
        flex-direction: column;
        align-items: stretch;
      }

      .search-container, .filter-container {
        min-width: unset;
        width: 100%;
      }

      .details-grid {
        grid-template-columns: 1fr;
      }

      :host ::ng-deep .p-datatable-wrapper {
        overflow-x: auto;
      }

      :host ::ng-deep .p-datatable-table {
        min-width: 800px;
      }

      .table-container {
        padding: 0 0.5rem 2rem;
      }
    }

    @media (max-width: 480px) {
      .hero-card {
        margin: 0 0.5rem;
      }

      .assessment-details {
        margin: 0.5rem;
        padding: 1rem;
      }
    }

    /* PrimeNG customizations */
    :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      background: #f8fafc;
      border-color: #e2e8f0;
      font-weight: 600;
    }

    :host ::ng-deep .p-datatable .p-datatable-tbody > tr:nth-child(even) {
      background: #f9fafb;
    }

    :host ::ng-deep .p-datatable .p-datatable-tbody > tr:hover {
      background: #f0f9ff;
    }

    :host ::ng-deep .p-progressbar {
      border-radius: 4px;
      overflow: hidden;
    }

    :host ::ng-deep .p-tag {
      font-size: 0.875rem;
      padding:10px;
      margin:10px;
    }

    :host ::ng-deep .p-button-sm {
      padding: 0.375rem 0.75rem;
    }
  `],
})
export class DashboardComponent implements OnInit {
  assessments: AssessmentResult[] = [];
  loading: boolean = false;
  selectedLevels: string[] = [];
  levelOptions = [
    { label: 'Seed', value: 'seed' },
    { label: 'Settlement', value: 'settlement' },
    { label: 'Organisation', value: 'organisation' },
    { label: 'Leadership', value: 'leadership' },
  ];
  expandedRows: { [key: string]: boolean } = {};

  constructor(
    private firebaseService: FirebaseService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    // Only fetch data if authenticated
    if (this.authService.isAuthenticated()) {
      this.getAssessmentResults();
    }
  }

  getAssessmentResults() {
    // Double-check authentication before fetching data
    if (!this.authService.isAuthenticated()) {
      console.warn('Unauthorized access attempt to assessment data');
      return;
    }

    this.loading = true;
    this.firebaseService.getAssessments().then(results => {
      this.assessments = results;
      this.loading = false;
    }).catch(error => {
      console.error('Error fetching assessment results:', error);
      this.loading = false;
    });
  }

  refreshData() {
    this.getAssessmentResults();
  }

  exportData() {
    // Simple CSV export
    const headers = [
      'Name', 'Email', 'Organisation', 'Total Score', 'Level', 
      'Intellectual Advice', 'Formal Qualifications', 'Financial Investment', 
      'Long Term Assets', 'Role Model', 'Network Connector', 
      'Leadership Roles', 'Mentor Trainer', 'Date'
    ];
    
    const csvData = this.assessments.map(assessment => [
      assessment.participantInfo?.name || 'Anonymous',
      assessment.participantInfo?.email || 'N/A',
      assessment.participantInfo?.organisation || 'N/A',
      assessment.totalScore,
      assessment.level,
      assessment.selfAssessment?.intellectualAdvice || 0,
      assessment.selfAssessment?.formalQualifications || 0,
      assessment.selfAssessment?.financialInvestment || 0,
      assessment.selfAssessment?.longTermAssets || 0,
      assessment.selfAssessment?.roleModel || 0,
      assessment.selfAssessment?.networkConnector || 0,
      assessment.selfAssessment?.leadershipRoles || 0,
      assessment.selfAssessment?.mentorTrainer || 0,
      new Date(assessment.timestamp).toLocaleDateString()
    ]);

    const csv = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nzf-assessments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  onLevelFilter(event: any) {
    // Custom filter logic for level selection
    if (this.selectedLevels && this.selectedLevels.length > 0) {
      // If levels are selected, filter assessments
      const filteredAssessments = this.assessments.filter(assessment => 
        this.selectedLevels.includes(assessment.level)
      );
      // You can implement custom table filtering here if needed
    }
  }

  getLevelSeverity(level: string): "success" | "info" | "warning" | "danger" | "secondary" | "contrast" | undefined {
    switch (level) {
      case 'Leadership Layer':
        return 'success';
      case 'Harvest Layer':
        return 'info';
      case 'Growth Layer':
        return 'warning';
      case 'Sprout Layer':
        return 'secondary';
      case 'Seed Layer':
        return 'contrast';
      default:
        return 'secondary';
    }
  }

  viewDetails(assessment: AssessmentResult) {
    // Toggle expanded row
    const key = assessment.timestamp.toString();
    this.expandedRows[key] = !this.expandedRows[key];
  }

  expandRow(assessment: AssessmentResult) {
    const key = assessment.timestamp.toString();
    this.expandedRows[key] = !this.expandedRows[key];
  }

  onGlobalFilter(event: Event) {
    const target = event.target as HTMLInputElement;
    return target?.value || '';
  }

  getFilteredAssessments(): AssessmentResult[] {
    if (!this.selectedLevels || this.selectedLevels.length === 0) {
      return this.assessments;
    }
    return this.assessments.filter(assessment => 
      this.selectedLevels.includes(assessment.level)
    );
  }
}